import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import HeroSearch from "@/components/HeroSearch";
import CampusTruthBanner from "@/components/RealTalk/CampusTruthBanner";
import LeadCaptureModal from "@/components/LeadCaptureModal";
import CollegeImage from "@/components/CollegeImage";
import { track } from "@/lib/analytics";
import { summarizeList } from "@/lib/utils";
import { websiteLd, organizationLd, itemListLd, courseSlug } from "@/lib/seo";
import ProductLadder from "@/components/ProductLadder";
import {
  Bot, UserCheck, ShieldCheck, Sparkles, ArrowRight, GraduationCap,
  Search, MapPin, BookOpen, HeartHandshake, Compass, MessageSquare,
  CheckSquare, ClipboardCheck, TrendingUp,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/1205651/pexels-photo-1205651.jpeg";

export default function Home() {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ colleges: 0, states: 0, cities: 0 });
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.get("/states"),
      api.get("/cities"),
      api.get("/courses"),
      api.get("/search", { params: { sort: "popularity", limit: 6 } }),
    ]).then(([st, ci, co, se]) => {
      if (!alive) return;
      if (st.status === "fulfilled") {
        const s = st.value.data.states || [];
        setStates(s);
        setStats((x) => ({ ...x, states: s.length, colleges: s.reduce((a, b) => a + (b.colleges || 0), 0) }));
      }
      if (ci.status === "fulfilled") { const c = ci.value.data.cities || []; setCities(c); setStats((x) => ({ ...x, cities: c.length })); }
      if (co.status === "fulfilled") setCourses(co.value.data.courses || []);
      if (se.status === "fulfilled") setFeatured(se.value.data.results || []);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, []);

  const popularStates = states.slice(0, 8);
  const popularCities = cities.slice(0, 12);
  const popularCourses = courses.slice(0, 12);

  const jsonLd = [websiteLd(), organizationLd()];
  if (popularStates.length) jsonLd.push(itemListLd("Popular States", popularStates.map((s) => ({ name: s.name, path: `/state/${s.slug}` }))));

  return (
    <div data-testid="home-page">
      <SEOHead
        title="Find Colleges Across India — AI-Powered Admission Guidance"
        description="Explore verified colleges across India by state, city and course. Get free AI-powered admission guidance and expert counselling to make the right admission decision."
        canonicalPath="/"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#FDF9F1] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#C08E3B]">
              <Sparkles className="h-3.5 w-3.5" /> India's AI Admission Decision Platform
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Find the Right College.{" "}
              <span className="text-[#0B2A6B]">Make the Right</span>{" "}
              <span className="text-[#D4A24C]">Decision.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Explore verified colleges across India with AI-powered admission guidance — search by state, city or course and get free expert help.
            </p>

            <div className="mt-7">
              <HeroSearch />
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link to="/ai-counseling" onClick={() => track("talk_to_ai_click", { location: "hero" })} data-testid="hero-cta-fit-check" className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B2A6B] px-5 py-3 text-sm font-semibold text-white hover:bg-[#081E4D] hover:-translate-y-0.5 transition-all">
                <Bot className="h-4 w-4" /> Start Free AI College Fit Check
              </Link>
              <Link to="/pricing" onClick={() => track("expert_counselling_click", { location: "hero" })} data-testid="hero-cta-expert" className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#D4A24C] px-5 py-3 text-sm font-semibold text-white hover:bg-[#C08E3B] transition-colors">
                <UserCheck className="h-4 w-4" /> Book Expert Admission Call – ₹499
              </Link>
              <Link to="/colleges" onClick={() => track("search_colleges_click", { location: "hero" })} data-testid="hero-cta-search" className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-[#0B2A6B] px-5 py-3 text-sm font-semibold text-[#0B2A6B] hover:bg-slate-50 transition-all">
                <Search className="h-4 w-4" /> Search Colleges
              </Link>
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200">
              <img src={HERO_IMG} alt="Students choosing the right college in India" className="w-full h-[460px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 flex items-center gap-3 rounded-2xl bg-white p-4 shadow-xl ring-1 ring-slate-100">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0B2A6B]"><GraduationCap className="h-5 w-5 text-white" /></span>
              <div>
                <p className="text-sm font-bold text-slate-900">{stats.colleges ? `${stats.colleges}+ Colleges` : "Verified Colleges"}</p>
                <p className="text-xs text-slate-500">Across {stats.states || "multiple"} states · verified data</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Campus Truth homepage banner (post-hero, before trust indicators) */}
      <CampusTruthBanner />

      {/* Trust indicators */}
      <section className="border-y border-slate-200 bg-white" data-testid="trust-indicators">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-5">
          {[
            { icon: ShieldCheck, t: "Verified Colleges", d: "Real, audited data — never invented" },
            { icon: Bot, t: "AI-Powered Guidance", d: "Instant, personalised recommendations" },
            { icon: Compass, t: "Student-Focused", d: "Built to help you decide with confidence" },
            { icon: UserCheck, t: "Expert Counsellors", d: "Human help whenever you need it" },
          ].map((b) => (
            <div key={b.t} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0B2A6B]/10"><b.icon className="h-5 w-5 text-[#0B2A6B]" /></span>
              <div>
                <p className="text-sm font-bold text-slate-900">{b.t}</p>
                <p className="text-xs text-slate-500">{b.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-[#0B2A6B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
          {[
            { k: stats.colleges ? `${stats.colleges}+` : "—", v: "Verified Colleges" },
            { k: stats.states ? `${stats.states}` : "—", v: "States Covered" },
            { k: stats.cities ? `${stats.cities}+` : "—", v: "Cities" },
            { k: "24/7", v: "AI Guidance" },
          ].map((s) => (
            <div key={s.v}>
              <p className="font-display text-3xl sm:text-4xl font-extrabold text-[#D4A24C]">{s.k}</p>
              <p className="mt-1 text-xs sm:text-sm text-white/70">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Popular States */}
      {(!loaded || popularStates.length > 0) && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="popular-states">
        <SectionHead eyebrow="Explore by location" title="Popular States" link="/colleges" />
        {!loaded ? <GridSkeleton count={8} cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" h="h-16" /> : (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {popularStates.map((s) => (
            <Link key={s.slug} to={`/state/${s.slug}`} data-testid={`state-card-${s.slug}`} className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3.5 hover:border-[#0B2A6B]/40 hover:shadow-sm transition-all">
              <div>
                <p className="font-semibold text-slate-800 group-hover:text-[#0B2A6B]">{s.name}</p>
                <p className="text-xs text-slate-500">{s.colleges} colleges · {s.cities} cities</p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-[#0B2A6B] group-hover:translate-x-0.5 transition-all" />
            </Link>
          ))}
        </div>
        )}
      </section>
      )}

      {/* Popular Cities */}
      {(!loaded || popularCities.length > 0) && (
      <section className="bg-slate-50 border-y border-slate-200" data-testid="popular-cities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <SectionHead eyebrow="Top student cities" title="Popular Cities" />
          {!loaded ? <ChipSkeleton count={12} /> : (
          <div className="mt-8 flex flex-wrap gap-2.5">
            {popularCities.map((c) => (
              <Link key={c.slug} to={`/city/${c.slug}`} data-testid={`city-chip-${c.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#0B2A6B]/40 hover:text-[#0B2A6B] transition-colors">
                <MapPin className="h-3.5 w-3.5 text-[#D4A24C]" /> {c.name} <span className="text-slate-400">({c.colleges})</span>
              </Link>
            ))}
          </div>
          )}
        </div>
      </section>
      )}

      {/* Popular Courses */}
      {(!loaded || popularCourses.length > 0) && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="popular-courses">
        <SectionHead eyebrow="Find by programme" title="Popular Courses" />
        {!loaded ? <ChipSkeleton count={12} /> : (
        <div className="mt-8 flex flex-wrap gap-2.5">
          {popularCourses.map((c) => (
            <Link key={c.name} to={`/course/${courseSlug(c.name)}`} data-testid={`course-chip-${courseSlug(c.name)}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#0B2A6B]/40 hover:text-[#0B2A6B] transition-colors">
              <BookOpen className="h-3.5 w-3.5 text-[#0B2A6B]" /> {c.name} <span className="text-slate-400">({c.count})</span>
            </Link>
          ))}
        </div>
        )}
      </section>
      )}

      {/* Featured Colleges */}
      {(!loaded || featured.length > 0) && (
      <section className="bg-slate-50 border-y border-slate-200" data-testid="featured-colleges">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <SectionHead eyebrow="Top picks" title="Featured Colleges" link="/colleges" linkLabel="View all colleges" />
          {!loaded ? <GridSkeleton count={6} cols="sm:grid-cols-2 lg:grid-cols-3" h="h-56" /> : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((c) => <FeaturedCard key={c.slug} c={c} />)}
          </div>
          )}
        </div>
      </section>
      )}

      {/* How CollegeUnlock Helps You */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="how-it-works">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Your journey</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">How CollegeUnlock Helps You</h2>
          <p className="mt-3 text-slate-600">A clear, four-step path from doubt to a confident admission decision.</p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { n: 1, icon: CheckSquare, title: "Check Your College Fit", body: "Free AI Fit Check: dream, target and safe recommendations for your profile.", cta: "/ai-counseling", ctaText: "Start Free" },
            { n: 2, icon: TrendingUp, title: "Understand Your Risk", body: "₹99 Regret Risk Score™ report with clear next steps and PDF delivery.", cta: "/products/regret-risk-report", ctaText: "See ₹99 Report" },
            { n: 3, icon: UserCheck, title: "Speak to an Expert", body: "₹499 Expert Admission Clarity Call — human review of your shortlist.", cta: "/products/expert-call", ctaText: "See ₹499 Call" },
            { n: 4, icon: ClipboardCheck, title: "Finalize Your Plan", body: "₹999 Admission Decision Pack: written, human-reviewed final plan.", cta: "/products/decision-pack", ctaText: "See ₹999 Pack" },
          ].map((s) => (
            <div key={s.n} className="relative rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-sm transition-shadow">
              <span className="absolute -top-3 left-6 rounded-full bg-[#0B2A6B] px-2.5 py-0.5 text-[10px] font-bold text-white">STEP {s.n}</span>
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FDF9F1] text-[#C08E3B]"><s.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display text-base font-bold text-slate-900">{s.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600">{s.body}</p>
              <Link to={s.cta} className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-[#0B2A6B]">{s.ctaText} <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          ))}
        </div>
      </section>

      {/* Product Ladder */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <ProductLadder />
      </section>

      {/* Final CTA */}
      <section className="bg-[#0B2A6B]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Start making your admission decision today</h2>
          <p className="mt-4 text-base text-white/70 max-w-xl mx-auto">Free AI Fit Check first. Add a paid report or expert call whenever you want a human review.</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/ai-counseling" onClick={() => track("talk_to_ai_click", { location: "footer_cta" })} data-testid="final-cta-fit-check" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-semibold text-[#0B2A6B] hover:bg-slate-100 transition-colors">
              <Bot className="h-4 w-4" /> Start Free AI College Fit Check
            </Link>
            <Link to="/pricing" onClick={() => track("cta_click", { cta: "footer_pricing" })} data-testid="final-cta-pricing" className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#D4A24C] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#C08E3B] transition-colors">
              <UserCheck className="h-4 w-4" /> See Paid Products
            </Link>
            <Link to="/colleges" onClick={() => track("search_colleges_click", { location: "footer_cta" })} data-testid="final-cta-search" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 px-6 py-3.5 text-sm font-semibold text-white ring-1 ring-white/30 hover:bg-white/20 transition-colors">
              <Search className="h-4 w-4" /> Search Colleges
            </Link>
          </div>
        </div>
      </section>

      <LeadCaptureModal open={guidanceOpen} intent="admission_guidance" college={null} onClose={() => setGuidanceOpen(false)} />
    </div>
  );
}

function GridSkeleton({ count, cols, h }) {
  return <div className={`mt-8 grid ${cols} gap-3`}>{Array.from({ length: count }).map((_, i) => <div key={i} className={`${h} rounded-xl bg-slate-100 animate-pulse`} />)}</div>;
}
function ChipSkeleton({ count }) {
  return <div className="mt-8 flex flex-wrap gap-2.5">{Array.from({ length: count }).map((_, i) => <div key={i} className="h-9 w-28 rounded-full bg-slate-100 animate-pulse" />)}</div>;
}

function SectionHead({ eyebrow, title, link, linkLabel = "View all" }) {
  return (
    <div className="flex items-end justify-between">
      <div>
        <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">{eyebrow}</span>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
      </div>
      {link && (
        <Link to={link} className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-[#0B2A6B] hover:gap-2 transition-all">
          {linkLabel} <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

function FeaturedCard({ c }) {
  const topTier = (c.tier || "").toLowerCase().includes("tier 1");
  return (
    <Link to={`/college/${c.slug}`} data-testid={`featured-college-${c.slug}`} className="group block overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(11,35,82,0.04)] ring-1 ring-transparent transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-1 hover:shadow-[0_18px_40px_-16px_rgba(11,35,82,0.28)] hover:ring-[#D4A24C]/25">
      <div className="h-36 w-full overflow-hidden bg-slate-100">
        <CollegeImage src={c.image} alt={c.name} label={c.category || c.vertical || "Institute"} />
      </div>
      <div className="p-5">
        <h3 className="font-display text-base font-bold text-slate-900 leading-snug line-clamp-2">{c.name}</h3>
        {(c.category || c.vertical) && <p className="mt-1.5 truncate text-[13px] font-medium text-[#C08E3B]" title={c.category || c.vertical}>{summarizeList(c.category || c.vertical, 1)}{topTier ? " · Top Tier" : ""}</p>}
        <p className="mt-1.5 flex items-center gap-1 text-[13px] text-slate-500"><MapPin className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{[c.city, c.state].filter(Boolean).join(", ")}</span></p>
        <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-[#0B2A6B]">Explore College <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></span>
      </div>
    </Link>
  );
}
